import React, { useState, useRef } from 'react';
import { Row, Col, Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useQuery, useMutation } from 'react-query';
import { useReactToPrint } from 'react-to-print';
import { FaPrint, FaCalendarAlt, FaFilter, FaUsers, FaLayerGroup, FaEye, FaIdCard } from 'react-icons/fa';
import { cartePointageService, agentService, groupeService } from '../services/api';
import CartePointage4ParPage from '../components/CartePointage4ParPage';
import CarteProfessionnelle from '../components/CarteProfessionnelle';
import LogoUploadModal from '../components/LogoUploadModal';
import PhotoTest from '../components/PhotoTest';
import AgentDataTest from '../components/AgentDataTest';
import DiagnosticCartes from '../components/DiagnosticCartes';

const CartesPointage = () => {
  const [filtres, setFiltres] = useState({
    mois: new Date().getMonth() + 1,
    annee: new Date().getFullYear(),
    typeFiltre: 'tous', // 'tous', 'agent', 'groupe', 'fonction'
    agentId: '',
    groupeId: '',
    fonction: ''
  });
  
  const [cartes, setCartes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [modeAffichage, setModeAffichage] = useState('impression4'); // 'impression4' par défaut
  const [logoEntreprise, setLogoEntreprise] = useState(null);
  const [logoEcoti, setLogoEcoti] = useState(null);
  const [logoSecondaire, setLogoSecondaire] = useState(null);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [logoModalType, setLogoModalType] = useState('ecoti');
  const printRef = useRef();
  const printMultipleRef = useRef();
  const print4ParPageRef = useRef();
  const printProfessionnelleRef = useRef();

  // Fonction pour charger le logo
  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoEntreprise(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour gérer l'upload des logos
  const handleLogoUpload = (type, logoUrl) => {
    if (type === 'ecoti') {
      setLogoEcoti(logoUrl);
    } else if (type === 'secondaire') {
      setLogoSecondaire(logoUrl);
    }
  };

  // Fonction pour ouvrir le modal de logo
  const openLogoModal = (type) => {
    setLogoModalType(type);
    setShowLogoModal(true);
  };

  // Queries pour les données de référence
  const { data: agents = [] } = useQuery('agents', () => 
    agentService.getAll({ actif: true }).then(res => res.data)
  );

  const { data: groupes = [] } = useQuery('groupes', () => 
    groupeService.getAll().then(res => res.data)
  );

  const { data: fonctions = [] } = useQuery('fonctions', () => 
    agentService.getFonctions().then(res => res.data)
  );

  // Mutation pour générer les cartes
  const genererCartesMutation = useMutation(
    async () => {
      const { mois, annee, typeFiltre, agentId, groupeId, fonction } = filtres;
      
      switch (typeFiltre) {
        case 'agent':
          if (!agentId) throw new Error('Veuillez sélectionner un agent');
          const response = await cartePointageService.genererCarteAgent(agentId, mois, annee);
          return [response.data]; // Retourner un tableau avec une seule carte
          
        case 'groupe':
          if (!groupeId) throw new Error('Veuillez sélectionner un groupe');
          const responseGroupe = await cartePointageService.genererCartesGroupe(groupeId, mois, annee);
          return responseGroupe.data;
          
        case 'fonction':
          if (!fonction) throw new Error('Veuillez sélectionner une fonction');
          const responseFonction = await cartePointageService.genererCartesFonction(fonction, mois, annee);
          return responseFonction.data;
          
        default:
          const responseTous = await cartePointageService.genererToutesLesCartes(mois, annee);
          return responseTous.data;
      }
    },
    {
      onSuccess: (data) => {
        setCartes(data);
        setIsGenerating(false);
      },
      onError: (error) => {
        setIsGenerating(false);
        console.error('Erreur génération:', error);
      }
    }
  );

  const handleGenererCartes = async () => {
    setIsGenerating(true);
    setCartes([]);
    await genererCartesMutation.mutateAsync();
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Cartes_Pointage_${filtres.mois}_${filtres.annee}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 1cm;
      }
      @media print {
        .no-print { display: none !important; }
        .page-break { page-break-before: always; }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `
  });

  const handlePrintMultiple = useReactToPrint({
    content: () => printMultipleRef.current,
    documentTitle: `Cartes_Pointage_12_par_page_${filtres.mois}_${filtres.annee}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 3mm;
      }
      @media print {
        .no-print { display: none !important; }
        html, body { 
          -webkit-print-color-adjust: exact !important; 
          color-adjust: exact !important;
          print-color-adjust: exact !important;
          font-size: 12px !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        img {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `
  });

  const handlePrint4ParPage = useReactToPrint({
    content: () => print4ParPageRef.current,
    documentTitle: `Cartes_Pointage_4_par_page_${filtres.mois}_${filtres.annee}`,
    pageStyle: `
      @page {
        size: A4 landscape !important;
        margin: 2mm !important;
      }
      @media print {
        .no-print { display: none !important; }
        .page-break { page-break-before: always; }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 297mm !important;
          height: 210mm !important;
        }
        html {
          margin: 0 !important;
          padding: 0 !important;
          width: 297mm !important;
          height: 210mm !important;
        }
        @page {
          size: A4 landscape !important;
          margin: 2mm !important;
        }
      }
    `
  });

  const handlePrintProfessionnelle = useReactToPrint({
    content: () => printProfessionnelleRef.current,
    documentTitle: `Cartes_Professionnelles_8_par_page_${filtres.mois}_${filtres.annee}`,
    pageStyle: `
      @page {
        size: A4 !important;
        margin: 2mm !important;
      }
      @media print {
        .no-print { display: none !important; }
        .page-break { page-break-before: always; }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 210mm !important;
          height: 297mm !important;
          font-size: 14px !important;
        }
        html {
          margin: 0 !important;
          padding: 0 !important;
          width: 210mm !important;
          height: 297mm !important;
        }
        img {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
          max-width: 100% !important;
          height: auto !important;
        }
        @page {
          size: A4 !important;
          margin: 2mm !important;
        }
      }
    `
  });

  const handleFiltreChange = (field, value) => {
    setFiltres(prev => ({ ...prev, [field]: value }));
    setCartes([]); // Vider les cartes quand les filtres changent
  };

  const getMoisNom = (numeroMois) => {
    const mois = [
      '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return mois[numeroMois] || '';
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Cartes de Pointage</h2>
          <p className="text-muted">Générez et imprimez les cartes de pointage mensuelles</p>
        </div>
        {cartes.length > 0 && (
          <div className="d-flex gap-2">
            <Button 
              variant={modeAffichage === 'impression4' ? 'primary' : 'outline-primary'} 
              onClick={() => setModeAffichage('impression4')} 
              className="no-print"
            >
              <FaEye className="me-2" />
              Aperçu 4 par page
            </Button>
            <Button 
              variant={modeAffichage === 'professionnelle' ? 'primary' : 'outline-primary'} 
              onClick={() => setModeAffichage('professionnelle')} 
              className="no-print"
            >
              <FaIdCard className="me-2" />
              Aperçu Cartes Professionnelles
            </Button>
            <Button variant="outline-success" onClick={handlePrint4ParPage} className="no-print">
              <FaPrint className="me-2" />
              Imprimer 4 par page (Paysage)
            </Button>
            <Button variant="outline-info" onClick={handlePrintProfessionnelle} className="no-print">
              <FaPrint className="me-2" />
              Imprimer Cartes Professionnelles (8 par page)
            </Button>
          </div>
        )}
      </div>

      {/* Filtres de génération */}
      <Card className="mb-4 no-print">
        <Card.Header>
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Critères de génération
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            {/* Période */}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaCalendarAlt className="me-1" />
                  Mois
                </Form.Label>
                <Form.Select
                  value={filtres.mois}
                  onChange={(e) => handleFiltreChange('mois', parseInt(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {getMoisNom(i + 1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Année</Form.Label>
                <Form.Control
                  type="number"
                  value={filtres.annee}
                  onChange={(e) => handleFiltreChange('annee', parseInt(e.target.value))}
                  min={2020}
                  max={2030}
                />
              </Form.Group>
            </Col>

            {/* Type de filtre */}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Générer pour</Form.Label>
                <Form.Select
                  value={filtres.typeFiltre}
                  onChange={(e) => handleFiltreChange('typeFiltre', e.target.value)}
                >
                  <option value="tous">Tous les agents actifs</option>
                  <option value="agent">Un agent spécifique</option>
                  <option value="groupe">Un groupe</option>
                  <option value="fonction">Une fonction</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Filtre spécifique */}
            <Col md={3}>
              {filtres.typeFiltre === 'agent' && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaUsers className="me-1" />
                    Agent
                  </Form.Label>
                  <Form.Select
                    value={filtres.agentId}
                    onChange={(e) => handleFiltreChange('agentId', e.target.value)}
                  >
                    <option value="">Sélectionner un agent</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.nom} {agent.prenoms}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {filtres.typeFiltre === 'groupe' && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaLayerGroup className="me-1" />
                    Groupe
                  </Form.Label>
                  <Form.Select
                    value={filtres.groupeId}
                    onChange={(e) => handleFiltreChange('groupeId', e.target.value)}
                  >
                    <option value="">Sélectionner un groupe</option>
                    {groupes.map(groupe => (
                      <option key={groupe.id} value={groupe.id}>
                        {groupe.nom}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {filtres.typeFiltre === 'fonction' && (
                <Form.Group className="mb-3">
                  <Form.Label>Fonction</Form.Label>
                  <Form.Select
                    value={filtres.fonction}
                    onChange={(e) => handleFiltreChange('fonction', e.target.value)}
                  >
                    <option value="">Sélectionner une fonction</option>
                    {fonctions.map(fonction => (
                      <option key={fonction} value={fonction}>
                        {fonction}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Badge bg="info">
                Période: du 21/{filtres.mois === 1 ? 12 : filtres.mois - 1}/{filtres.mois === 1 ? filtres.annee - 1 : filtres.annee} 
                {' '}au 20/{filtres.mois}/{filtres.annee}
              </Badge>
            </div>
            <Button 
              variant="primary" 
              onClick={handleGenererCartes}
              disabled={isGenerating}
            >
              {isGenerating ? 'Génération...' : 'Générer les Cartes'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Messages d'erreur */}
      {genererCartesMutation.error && (
        <Alert variant="danger" className="no-print">
          Erreur: {genererCartesMutation.error.response?.data || genererCartesMutation.error.message}
        </Alert>
      )}

      {/* Aperçu des cartes */}
      {cartes.length > 0 && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3 no-print">
            <h4>
              {modeAffichage === 'professionnelle' 
                ? `Aperçu Cartes Professionnelles (${cartes.length})`
                : `Aperçu d'impression 4 par page (${cartes.length})`
              }
            </h4>
            <div className="d-flex align-items-center gap-3">
              {modeAffichage === 'professionnelle' ? (
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => openLogoModal('ecoti')}
                  >
                    <FaIdCard className="me-1" />
                    Logo ECOTI
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => openLogoModal('secondaire')}
                  >
                    <FaIdCard className="me-1" />
                    Logo Secondaire
                  </Button>
                </div>
              ) : (
                <Form.Group className="mb-0">
                  <Form.Label className="me-2">Logo de l'entreprise:</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    style={{ width: 'auto' }}
                  />
                </Form.Group>
              )}
              <small className="text-muted">
                {modeAffichage === 'professionnelle' 
                  ? 'Cet aperçu correspond exactement à ce qui sera imprimé (8 cartes par page A4 portrait)'
                  : 'Cet aperçu correspond exactement à ce qui sera imprimé (4 cartes par page A4 paysage)'
                }
              </small>
            </div>
          </div>

          {/* Affichage selon le mode sélectionné */}
          <div>
            {modeAffichage === 'professionnelle' ? (
              <CarteProfessionnelle 
                cartes={cartes} 
                isPreview={true} 
                logoEcotiUrl={logoEcoti}
                logoSecondaireUrl={logoSecondaire}
              />
            ) : (
              <CartePointage4ParPage 
                cartes={cartes} 
                isPreview={true} 
                logoEntreprise={logoEntreprise} 
              />
            )}
          </div>

          {/* Composants d'impression (cachés) */}
          <div style={{ display: 'none' }}>
            <div ref={print4ParPageRef}>
              <CartePointage4ParPage cartes={cartes} isPreview={false} logoEntreprise={logoEntreprise} />
            </div>
            <div ref={printProfessionnelleRef}>
              <CarteProfessionnelle 
                cartes={cartes} 
                isPreview={false} 
                logoEcotiUrl={logoEcoti}
                logoSecondaireUrl={logoSecondaire}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal pour l'upload des logos */}
      <LogoUploadModal
        show={showLogoModal}
        onHide={() => setShowLogoModal(false)}
        logoType={logoModalType}
        currentLogoUrl={logoModalType === 'ecoti' ? logoEcoti : logoSecondaire}
        onLogoUpload={handleLogoUpload}
      />


      {/* DIAGNOSTIC DES CARTES - TOUJOURS VISIBLE */}
      {cartes.length > 0 && (
        <div className="no-print">
          <DiagnosticCartes cartes={cartes} />
        </div>
      )}

      {/* Composant de test des données des agents - à supprimer en production */}
      {process.env.NODE_ENV === 'development' && cartes.length > 0 && (
        <Card className="mb-4 no-print">
          <Card.Header>
            <h5>Test des Données des Agents (Développement)</h5>
          </Card.Header>
          <Card.Body>
            <AgentDataTest cartes={cartes} />
          </Card.Body>
        </Card>
      )}

      {/* Composant de test des photos - à supprimer en production */}
      {process.env.NODE_ENV === 'development' && cartes.length > 0 && (
        <Card className="mb-4 no-print">
          <Card.Header>
            <h5>Test des Photos (Développement)</h5>
          </Card.Header>
          <Card.Body>
            <div className="row">
              {cartes.slice(0, 3).map((carte, index) => (
                <div key={index} className="col-md-4">
                  <PhotoTest agent={carte.agent} />
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {cartes.length === 0 && !isGenerating && (
        <Card className="text-center">
          <Card.Body className="py-5">
            <FaPrint size={48} className="text-muted mb-3" />
            <h5>Aucune carte générée</h5>
            <p className="text-muted">
              Configurez les critères ci-dessus et cliquez sur "Générer les Cartes" pour commencer.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CartesPointage; 